import { analyticsBlocked, analyticsPageContext, isPrivateAnalyticsUrl, sanitizeAnalyticsParams } from './analyticsPrivacy';

// Install before React or any tracking library. Crossing the privacy boundary uses
// a fresh document so an already-executed third-party script cannot inspect the URL.
export const initializeAnalytics = () => {
  const privateDocument = isPrivateAnalyticsUrl(window.location.href);
  window.__havenAnalyticsBlocked = privateDocument;
  const crossesBoundary = (url: string | URL | null | undefined) =>
    url != null && isPrivateAnalyticsUrl(String(url)) !== privateDocument;

  for (const method of ['pushState', 'replaceState'] as const) {
    const original = window.history[method].bind(window.history);
    window.history[method] = (data, unused, url) => {
      if (crossesBoundary(url)) {
        window.__havenAnalyticsBlocked = true;
        if (method === 'replaceState') window.location.replace(String(url));
        else window.location.assign(String(url));
        return;
      }
      original(data, unused, url);
    };
  }
  window.addEventListener('pageshow', event => {
    if (event.persisted) window.__havenAnalyticsBlocked = isPrivateAnalyticsUrl(window.location.href);
  });
  window.addEventListener('popstate', event => {
    if (isPrivateAnalyticsUrl(window.location.href) !== privateDocument) {
      window.__havenAnalyticsBlocked = true;
      event.stopImmediatePropagation();
      window.location.reload();
    }
  }, true);
  window.addEventListener('click', event => {
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') as HTMLAnchorElement | null : null;
    if (!anchor || !isPrivateAnalyticsUrl(anchor.href)) return;
    // Prevent analytics click listeners from collecting a credential-bearing href.
    event.stopImmediatePropagation();
    if (!anchor.target && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
      event.preventDefault();
      window.location.assign(anchor.href);
    }
  }, true);

  window.dataLayer = [];
  window.gtag = (...args) => {
    if (analyticsBlocked()) return;
    const [command, target, config] = args;
    const payload = command === 'config' || command === 'event'
      ? { ...sanitizeAnalyticsParams(config), ...analyticsPageContext() }
      : config;
    // gtag's queue consumes an arguments object, rather than a plain array.
    // eslint-disable-next-line prefer-rest-params -- Google gtag expects an Arguments queue entry.
    (function (..._values: unknown[]) { window.dataLayer.push(arguments); })(command, target, payload);
  };
  if (privateDocument) return;

  window.gtag('js', new Date());
  window.gtag('consent', 'default', {
    analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied',
    ad_personalization: 'denied', wait_for_update: 500,
  });
  try {
    const consent = JSON.parse(localStorage.getItem('haven_cookie_consent') || 'null');
    if (consent?.version === '1.0' && ['accepted', 'custom'].includes(consent.status)) {
      window.gtag('consent', 'update', {
        analytics_storage: consent.preferences.analytics ? 'granted' : 'denied',
        ad_storage: consent.preferences.marketing ? 'granted' : 'denied',
        ad_user_data: consent.preferences.marketing ? 'granted' : 'denied',
        ad_personalization: consent.preferences.marketing ? 'granted' : 'denied',
      });
    }
  } catch { /* Missing or malformed consent stays denied. */ }
  window.gtag('config', 'G-SJF1CP3VGH', { send_page_view: false });
  let loaded = false;
  const load = () => {
    if (loaded || analyticsBlocked()) return;
    loaded = true;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-SJF1CP3VGH';
    document.head.appendChild(script);
  };
  if (document.readyState === 'complete') window.setTimeout(load, 1200);
  else window.addEventListener('load', () => window.setTimeout(load, 1200), { once: true });
  for (const event of ['scroll', 'pointerdown', 'keydown']) {
    window.addEventListener(event, load, { once: true, passive: true });
  }
};
